import { TraineeOnBoardTimeline } from '@ecdlink/graphql';
import { useMemo } from 'react';

export type UUID = string;

export type Practitioner = {
  id: UUID;
  userId: UUID;
  isActive: boolean;
  insertedDate: string | Date;
  isTrainee?: boolean | null;
  isRegistered?: boolean | null;
  isPrincipal?: boolean | null;
  isFundaAppAdmin?: boolean | null;
  isOnStipend?: boolean | null;
  startDate?: string | Date | null;
  coachHierarchy?: UUID | null;
  principalHierarchy?: UUID | null;
  attendedChildProgress?: boolean | null;
  attendedBusinessSkills?: boolean | null;
  programmeType?: string | null;
  progress?: number;
  shareInfo?: boolean | null;
  user?: {
    id: UUID;
    lastSeen?: string | Date;
    fullName?: string;
    idNumber?: string;
  } | null;
  visits?: Visit[];
  trainee?: TraineeOnBoardTimeline & {
    starterLicenceDate?: string | Date | null;
  };
};

export type VisitType = {
  name: string;
  normalizedName?: string;
  order?: number;
};

export type Visit = {
  id: UUID;
  practitionerId?: UUID;
  visitType: VisitType;
  plannedVisitDate: string | Date;
  actualVisitDate?: string | Date | null;
  attended?: boolean;
  insertedDate?: string | Date;
};

export type Absentee = {
  id?: UUID;
  userId?: UUID; // practitioner user id
  absentDate: string | Date;
  absentDateEnd?: string | Date | null;
  reason?: string;
};

export type License = {
  id?: UUID;
  userId?: UUID;
  delicensedDate?: string | Date | null;
};

export type PQARating = {
  id?: UUID;
  visitId?: UUID;
  overallRatingColor?: string | null;
};

export type ClubMember = {
  practitionerId: UUID;
  clubId: UUID;
  isActive?: boolean;
};

export type ClubMeetingRegister = {
  practitionerId: UUID;
  attended?: boolean;
  attendedDate?: string | Date;
};

export type Classroom = { id: UUID; userId?: UUID; name?: string };
export type ClassroomGroup = {
  id: UUID;
  classroomId: UUID;
  isActive?: boolean;
  userId?: UUID;
  learners?: any[];
};

export type Document = {
  id: UUID;
  userId?: UUID;
  documentTypeId?: UUID;
  isActive: boolean;
};

export type ChildProgressReportSummary = { reportsMissingOrIncomplete: number };

export type IncomeStatement = {
  balance: number;
  autoSubmitted?: boolean;
} | null;

export type NotificationDisplay = {
  userId?: UUID | null;
  userType?: string;
  subject: string;
  icon?: string;
  color?: string;
  message?: string;
  notes?: string;
  groupingName?: string;
};

const MetricsIconEnum = {
  Error: 'Error',
  Warning: 'Warning',
  Success: 'Success',
  None: 'None',
};
const MetricsColorEnum = {
  Error: 'Error',
  Warning: 'Warning',
  Success: 'Success',
  None: 'None',
};

const DEFAULT_NOW = () => new Date();

export function usePractitionerNotifications(options: {
  practitioners: Practitioner[] | undefined;
  mode: 'coach' | 'principal' | 'self' | string;
  now?: Date;
  removals?: {
    userId: UUID;
    isActive: boolean;
    insertedDate?: string | Date;
    dateOfRemoval?: string | Date;
  }[];
  licenses?: License[];
  visitData?: any[];
  pqaRatings?: PQARating[];
  absentees?: Absentee[];
  clubMembers?: ClubMember[];
  clubMeetingRegisters?: ClubMeetingRegister[];
  classrooms?: Classroom[];
  classroomGroups?: ClassroomGroup[];
  documents?: Document[];
  childProgressReportService?: {
    getReportOverDueStart: (year: number, isPeriod1: boolean) => Date;
    getReportOverDueEnd: (year: number, isPeriod1: boolean) => Date;
    getChildProgressReportStatusCountsForPractitioner?: (
      hierarchy: string | undefined,
      classroomGroupIds: UUID[]
    ) => ChildProgressReportSummary;
  } | null;
  attendanceService?: {
    getAllLearnerGroupInstances?: (groupId: UUID) => any[];
  } | null;
  incomeStatements?: { [userId: string]: IncomeStatement[] } | null;
  holidayService?: { isBusinessDay?: (d: Date) => boolean } | null;
}) {
  const {
    practitioners,
    mode,
    now = DEFAULT_NOW(),
    removals = [],
    licenses = [],
    visitData = [],
    pqaRatings = [],
    absentees = [],
    clubMembers = [],
    clubMeetingRegisters = [],
    classrooms = [],
    classroomGroups = [],
    documents = [],
    childProgressReportService = null,
    attendanceService = null,
    incomeStatements = null,
  } = options;

  return useMemo<NotificationDisplay[]>(() => {
    const results: NotificationDisplay[] = [];

    const toDate = (v?: string | Date | null) =>
      v ? new Date(v as string) : null;

    const startOfPreviousMonth = (d: Date) =>
      new Date(d.getFullYear(), d.getMonth() - 1, 1);
    const endOfPreviousMonth = (d: Date) =>
      new Date(d.getFullYear(), d.getMonth(), 0, 23, 59, 59, 999);

    const previousMonthStart = startOfPreviousMonth(now);
    const previousMonthEnd = endOfPreviousMonth(now);

    const getUserRemovals = (userId?: UUID) =>
      removals.filter((r) => r.userId === userId && r.isActive);
    const getUserVisits = (practitionerId?: UUID) =>
      practitioners?.find((v) => v.userId === practitionerId)?.visits;
    const getUserLicenses = (userId?: UUID) =>
      licenses.filter((l) => l.userId === userId && l.delicensedDate != null);
    const getAbsenteesForUser = (userId?: UUID) =>
      absentees.filter((a) => a.userId === userId);
    const getClubMemberForPractitioner = (practitionerId: UUID) =>
      clubMembers.find(
        (c) => c.practitionerId === practitionerId && (c.isActive ?? true)
      );

    const safeFormatDate = (d?: Date | string | null) =>
      d ? new Date(d as string).toLocaleDateString() : '';

    const filteredPractitioners = practitioners?.filter((p) => {
      if (mode === 'coach') return !!p.coachHierarchy;
      if (mode === 'principal') return !!p.principalHierarchy;
      return true;
    });

    for (const practitioner of filteredPractitioners!) {
      const notificationBase: Partial<NotificationDisplay> = {
        userId: practitioner.userId,
        userType: 'practitioner',
      };

      const notRegisteredString = (() => {
        const userLastSeen = practitioner.user?.lastSeen
          ? toDate(practitioner.user.lastSeen)
          : null;
        if (
          practitioner.isTrainee &&
          userLastSeen &&
          practitioner.insertedDate
        ) {
          const inserted = toDate(practitioner.insertedDate) as Date;
          if (userLastSeen <= inserted) return ' (Not registered)';
        }
        if (!practitioner.isTrainee && !practitioner.isRegistered)
          return ' (Not registered)';
        return '';
      })();

      // OFFLINE ON FUNDA APP FOR 2 WEEKS
      const userLastSeen = practitioner.user?.lastSeen
        ? toDate(practitioner.user.lastSeen)
        : null;

      if (
        userLastSeen &&
        !notRegisteredString &&
        userLastSeen < new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000)
      ) {
        results.push({
          ...(notificationBase as NotificationDisplay),
          subject: 'Offline on Funda app for 2 weeks',
          icon: MetricsIconEnum.Error,
          color: MetricsColorEnum.Error,
          notes: `Last login was ${safeFormatDate(userLastSeen)}`,
          groupingName: 'Offline on Funda app for 2 weeks',
        });
        continue;
      }

      // REMOVED FROM PROGRAMME
      const removalHistory = getUserRemovals(practitioner.userId).sort(
        (a, b) =>
          (toDate(b.insertedDate)?.getTime() ?? 0) -
          (toDate(a.insertedDate)?.getTime() ?? 0)
      )[0];
      if (removalHistory) {
        results.push({
          ...(notificationBase as NotificationDisplay),
          subject: 'Removed from programme' + notRegisteredString,
          icon: MetricsIconEnum.Error,
          color: MetricsColorEnum.Error,
          notes: `Practitioner is leaving on ${safeFormatDate(
            removalHistory.dateOfRemoval as Date
          )}`,
          groupingName: 'Removed from programe',
        });
        continue;
      }

      // TRAINEE ONBOARDING + TASKS
      if (practitioner.isTrainee) {
        const traineeVisits = getUserVisits(practitioner.id);
        let warningCount = 0;
        const hasPqa1 = traineeVisits?.some((v) =>
          v.visitType?.name?.includes('pqa_visit_1')
        );
        if (!hasPqa1) warningCount++;
        const hasSmartSpace = traineeVisits?.some((v) =>
          v.visitType?.name?.includes('smart_space')
        );
        if (!hasSmartSpace) warningCount++;

        const starterLicenseDate = practitioner?.trainee?.starterLicenceDate
          ? (toDate(practitioner.trainee?.starterLicenceDate) as Date)
          : new Date(0);

        if (
          starterLicenseDate < new Date(now.getTime() - 365 * 24 * 3600 * 1000)
        ) {
          if (warningCount > 0) {
            results.push({
              ...(notificationBase as NotificationDisplay),
              subject: 'Remove Trainee' + notRegisteredString,
              icon: MetricsIconEnum.Error,
              color: MetricsColorEnum.Error,
              groupingName: 'Remove trainee',
            });
            continue;
          }
        } else if (
          starterLicenseDate < new Date(now.getTime() - 183 * 24 * 3600 * 1000)
        ) {
          if (warningCount > 0) {
            results.push({
              ...(notificationBase as NotificationDisplay),
              subject: 'Trainee onboarding incomplete' + notRegisteredString,
              icon: MetricsIconEnum.Error,
              color: MetricsColorEnum.Error,
              groupingName: 'Trainee onboarding incomplete',
            });
            continue;
          }
        } else {
          const overdueCount = trainingOverdueCountApprox(
            practitioner,
            traineeVisits!,
            now
          );
          if (overdueCount > 0) {
            results.push({
              ...(notificationBase as NotificationDisplay),
              subject:
                `${overdueCount} trainee onboarding tasks overdue` +
                notRegisteredString,
              icon: MetricsIconEnum.Error,
              color: MetricsColorEnum.Error,
              groupingName: 'Trainee onboarding tasks overdue',
            });
            continue;
          }
        }

        // NEW TRAINEE
        results.push({
          ...(notificationBase as NotificationDisplay),
          subject: 'New trainee' + notRegisteredString,
          icon: MetricsIconEnum.Success,
          color: MetricsColorEnum.Success,
          groupingName: 'New trainee',
        });
        continue;
      }

      const practitionerVisits = getUserVisits(practitioner.id);
      const firstPqaVisit = practitionerVisits?.find(
        (v) =>
          v.visitType?.name === 'pqa_visit_1' ||
          v.visitType?.name === 'pqa_visit_1'
      );

      // LICENSES + DELICENSE RULE
      const userLicenses = getUserLicenses(practitioner.userId);
      if (userLicenses.length > 0) {
        const redFlagVisit = visitData.some(
          (d) =>
            d.question === 'step16_q1' &&
            d.questionAnswer === 'yes' &&
            d.userId === practitioner.userId
        );
        if (redFlagVisit) {
          results.push({
            ...(notificationBase as NotificationDisplay),
            subject: 'Delicense SmartStarter' + notRegisteredString,
            icon: MetricsIconEnum.Error,
            color: MetricsColorEnum.Error,
            groupingName: 'Delicense SmartStarter',
          });
          continue;
        }

        const pqaRating1 = firstPqaVisit
          ? pqaRatings.find((x) => x.visitId === firstPqaVisit.id) ?? null
          : null;
        const errorCount = [pqaRating1].filter(
          (x) => x && x.overallRatingColor === MetricsColorEnum.Error
        ).length;
        if (errorCount >= 2) {
          results.push({
            ...(notificationBase as NotificationDisplay),
            subject: 'Delicense SmartStarter' + notRegisteredString,
            icon: MetricsIconEnum.Error,
            color: MetricsColorEnum.Error,
            groupingName: 'Delicense SmartStarter',
          });
          continue;
        }
      }

      // FIRST PQA OVERDUE
      if (
        firstPqaVisit &&
        !firstPqaVisit.attended &&
        toDate(firstPqaVisit.plannedVisitDate) &&
        toDate(firstPqaVisit.plannedVisitDate)! < now
      ) {
        results.push({
          ...(notificationBase as NotificationDisplay),
          subject: 'First PQA overdue' + notRegisteredString,
          icon: MetricsIconEnum.Error,
          color: MetricsColorEnum.Error,
          notes: safeFormatDate(toDate(firstPqaVisit.plannedVisitDate)),
          groupingName: 'First PQA overdue',
        });
        continue;
      }

      // PQA REACCREDITATION OVERDUE
      const firstAccreditationPqaVisit = practitionerVisits?.find((v) =>
        v.visitType?.name?.includes('re_accreditation')
      );

      if (
        firstAccreditationPqaVisit &&
        !firstAccreditationPqaVisit.attended &&
        toDate(firstAccreditationPqaVisit.plannedVisitDate)! < now
      ) {
        results.push({
          ...(notificationBase as NotificationDisplay),
          subject: 'PQA reaccreditation overdue' + notRegisteredString,
          icon: MetricsIconEnum.Error,
          color: MetricsColorEnum.Error,
          notes: safeFormatDate(
            toDate(firstAccreditationPqaVisit.plannedVisitDate)
          ),
          groupingName: 'PQA reaccreditation overdue',
        });
        continue;
      }

      // SITE VISIT OVERDUE/DUE
      const firstSiteVisit = practitionerVisits?.find((v) =>
        v.visitType?.name?.includes('first_site_visit')
      );
      if (
        firstSiteVisit &&
        !firstSiteVisit.actualVisitDate &&
        toDate(firstSiteVisit.plannedVisitDate)! < now
      ) {
        results.push({
          ...(notificationBase as NotificationDisplay),
          subject: 'First site visit overdue' + notRegisteredString,
          icon: MetricsIconEnum.Error,
          color: MetricsColorEnum.Error,
          notes: safeFormatDate(toDate(firstSiteVisit.plannedVisitDate)),
          groupingName: 'First site visist overdue',
        });
        continue;
      }
      const secondSiteVisit = practitionerVisits?.find((v) =>
        v.visitType?.name?.includes('second_site_visit')
      );
      if (
        secondSiteVisit &&
        !secondSiteVisit.actualVisitDate &&
        toDate(secondSiteVisit.plannedVisitDate)! < now
      ) {
        results.push({
          ...(notificationBase as NotificationDisplay),
          subject: 'Second site visit overdue' + notRegisteredString,
          icon: MetricsIconEnum.Error,
          color: MetricsColorEnum.Error,
          notes: safeFormatDate(toDate(secondSiteVisit.plannedVisitDate)),
          groupingName: 'Second site visit overdue',
        });
        continue;
      }

      // SMARTSPACE VISIT
      const smartSpaceVisit = practitionerVisits?.find((v) =>
        v.visitType?.name?.includes('smart_space')
      );
      if (
        smartSpaceVisit &&
        !smartSpaceVisit.actualVisitDate &&
        toDate(smartSpaceVisit.plannedVisitDate)! < now
      ) {
        results.push({
          ...(notificationBase as NotificationDisplay),
          subject: 'SmartSpace visit overdue' + notRegisteredString,
          icon: MetricsIconEnum.Error,
          color: MetricsColorEnum.Error,
          notes: safeFormatDate(toDate(smartSpaceVisit.plannedVisitDate)),
          groupingName: 'SmartSpace visit overdue',
        });
        continue;
      }

      // PQA DUE checks (within next 3 months)
      if (
        firstAccreditationPqaVisit &&
        !firstAccreditationPqaVisit.attended &&
        toDate(firstAccreditationPqaVisit.plannedVisitDate)! > now &&
        toDate(firstAccreditationPqaVisit.plannedVisitDate)! <
          new Date(now.getTime() + 90 * 24 * 3600 * 1000)
      ) {
        results.push({
          ...(notificationBase as NotificationDisplay),
          subject:
            `${safeFormatDate(
              toDate(firstAccreditationPqaVisit.plannedVisitDate)
            )}, PQA reaccreditation due` + notRegisteredString,
          icon: MetricsIconEnum.None,
          color: MetricsColorEnum.None,
          notes: safeFormatDate(
            toDate(firstAccreditationPqaVisit.plannedVisitDate)
          ),
          groupingName: 'PQA reaccreditation due',
        });
        continue;
      }

      if (
        firstPqaVisit &&
        !firstPqaVisit.attended &&
        toDate(firstPqaVisit.plannedVisitDate)! > now
      ) {
        results.push({
          ...(notificationBase as NotificationDisplay),
          subject:
            `${safeFormatDate(
              toDate(firstPqaVisit.plannedVisitDate)
            )}, First PQA due` + notRegisteredString,
          icon: MetricsIconEnum.None,
          color: MetricsColorEnum.None,
          notes: safeFormatDate(toDate(firstPqaVisit.plannedVisitDate)),
          groupingName: 'First PQA visit due',
        });
        continue;
      }

      if (
        smartSpaceVisit &&
        !smartSpaceVisit.actualVisitDate &&
        toDate(smartSpaceVisit.plannedVisitDate)! > now
      ) {
        results.push({
          ...(notificationBase as NotificationDisplay),
          subject:
            `${safeFormatDate(
              toDate(smartSpaceVisit.plannedVisitDate)
            )}, SmartSpace visit due` + notRegisteredString,
          icon: MetricsIconEnum.Error,
          color: MetricsColorEnum.Error,
          notes: safeFormatDate(toDate(smartSpaceVisit.plannedVisitDate)),
          groupingName: 'SmartSpace visit due',
        });
        continue;
      }

      if (
        firstSiteVisit &&
        !firstSiteVisit.actualVisitDate &&
        toDate(firstSiteVisit.plannedVisitDate)! > now
      ) {
        results.push({
          ...(notificationBase as NotificationDisplay),
          subject:
            `${safeFormatDate(
              toDate(firstSiteVisit.plannedVisitDate)
            )}, First site visit due` + notRegisteredString,
          icon: MetricsIconEnum.Error,
          color: MetricsColorEnum.Error,
          notes: safeFormatDate(toDate(firstSiteVisit.plannedVisitDate)),
          groupingName: 'First site visit due',
        });
        continue;
      }

      if (
        secondSiteVisit &&
        !secondSiteVisit.actualVisitDate &&
        toDate(secondSiteVisit.plannedVisitDate)! > now
      ) {
        results.push({
          ...(notificationBase as NotificationDisplay),
          subject:
            `${safeFormatDate(
              toDate(secondSiteVisit.plannedVisitDate)
            )}, Second site visit due` + notRegisteredString,
          icon: MetricsIconEnum.Error,
          color: MetricsColorEnum.Error,
          notes: safeFormatDate(toDate(secondSiteVisit.plannedVisitDate)),
          groupingName: 'Second site visit due',
        });
        continue;
      }

      // PQA FOLLOW UP and REACCREDITATION FOLLOWUP overdue
      const pqaFollowupVisit = practitionerVisits?.find((v) =>
        v.visitType?.name?.includes('pqa_visit_follow_up')
      );
      if (
        pqaFollowupVisit &&
        !pqaFollowupVisit.attended &&
        toDate(pqaFollowupVisit.plannedVisitDate)! < now
      ) {
        results.push({
          ...(notificationBase as NotificationDisplay),
          subject:
            `${safeFormatDate(
              toDate(pqaFollowupVisit.plannedVisitDate)
            )}, PQA follow up visit due` + notRegisteredString,
          icon: MetricsIconEnum.Error,
          color: MetricsColorEnum.Error,
          notes: safeFormatDate(toDate(pqaFollowupVisit.plannedVisitDate)),
          groupingName: 'PQA follow up visit due',
        });
        continue;
      }
      const reaccredFollowup = practitionerVisits?.find((v) =>
        v.visitType?.name?.includes('re_accreditation')
      );
      if (
        reaccredFollowup &&
        !reaccredFollowup.attended &&
        toDate(reaccredFollowup.plannedVisitDate)! < now
      ) {
        results.push({
          ...(notificationBase as NotificationDisplay),
          subject:
            `${safeFormatDate(
              toDate(reaccredFollowup.plannedVisitDate)
            )}, PQA reaccreditation follow up due` + notRegisteredString,
          icon: MetricsIconEnum.Error,
          color: MetricsColorEnum.Error,
          notes: safeFormatDate(toDate(reaccredFollowup.plannedVisitDate)),
          groupingName: 'PQA reaccreditation follow up due',
        });
        continue;
      }

      // PROGRESS REPORTS OVERDUE
      if (childProgressReportService) {
        const isPeriod1 = previousMonthStart.getMonth() <= 6; // months 0..11 => <=6 means July is month 6
        const reportStart = childProgressReportService.getReportOverDueStart(
          previousMonthStart.getFullYear(),
          isPeriod1
        );
        const reportEnd = childProgressReportService.getReportOverDueEnd(
          previousMonthStart.getFullYear(),
          isPeriod1
        );
        if (
          now > reportStart &&
          now < reportEnd &&
          childProgressReportService.getChildProgressReportStatusCountsForPractitioner
        ) {
          const practitionerClassroomGroupIds = classroomGroups
            .filter(
              (g) => g.userId && g.userId === practitioner.userId && g.isActive
            )
            .map((g) => g.id);
          const missedReports =
            childProgressReportService.getChildProgressReportStatusCountsForPractitioner(
              practitioner.coachHierarchy ?? undefined,
              practitionerClassroomGroupIds
            );
          if (missedReports.reportsMissingOrIncomplete > 0) {
            results.push({
              ...(notificationBase as NotificationDisplay),
              subject: 'Progress reports overdue' + notRegisteredString,
              icon: MetricsIconEnum.Error,
              color: MetricsColorEnum.Error,
              groupingName: 'Progress reports overdue',
            });
            continue;
          }
        }
      }

      // ABSENTEES checks
      const practitionerAbsenteeDays = getAbsenteesForUser(
        practitioner.userId
      ).filter(
        (a) =>
          toDate(a.absentDate)! >= previousMonthStart &&
          toDate(a.absentDate)! <= now &&
          a.reason !== 'Practitioner removed from programme'
      );
      if (
        practitionerAbsenteeDays.some(
          (x) => toDate(x.absentDate)!.toDateString() === now.toDateString()
        )
      ) {
        results.push({
          ...(notificationBase as NotificationDisplay),
          subject: 'On leave' + notRegisteredString,
          icon: MetricsIconEnum.Error,
          color: MetricsColorEnum.Error,
          groupingName: 'On leave',
        });
        continue;
      }

      if (
        practitionerAbsenteeDays.some(
          (x) =>
            toDate(x.absentDate)! <= now ||
            (x.absentDateEnd && toDate(x.absentDateEnd)! <= now)
        )
      ) {
        results.push({
          ...(notificationBase as NotificationDisplay),
          subject: 'On leave' + notRegisteredString,
          icon: MetricsIconEnum.Error,
          color: MetricsColorEnum.Error,
          groupingName: 'On leave',
        });
        continue;
      }

      if (practitionerAbsenteeDays.length > 4) {
        results.push({
          ...(notificationBase as NotificationDisplay),
          subject: 'Practitioner absent more than 25%' + notRegisteredString,
          icon: MetricsIconEnum.Error,
          color: MetricsColorEnum.Error,
          groupingName: 'On leave',
        });
        continue;
      }

      if (
        practitionerAbsenteeDays.length > 0 &&
        practitionerAbsenteeDays.length <= 5 &&
        practitionerAbsenteeDays.some(
          (x) => toDate(x.absentDate)! <= previousMonthEnd
        )
      ) {
        results.push({
          ...(notificationBase as NotificationDisplay),
          subject: 'Practitioner absent last month' + notRegisteredString,
          icon: MetricsIconEnum.Warning,
          color: MetricsColorEnum.Warning,
          groupingName: 'On leave',
        });
        continue;
      }
      // INCOME STATEMENT checks
      if (
        (practitioner.isPrincipal || practitioner.isFundaAppAdmin) &&
        incomeStatements
      ) {
        const statements = incomeStatements[practitioner.userId ?? ''] ?? [];
        const lastMonthStatement = statements.find((s) => true) ?? null;
        if (!lastMonthStatement || lastMonthStatement.autoSubmitted) {
          results.push({
            ...(notificationBase as NotificationDisplay),
            subject: 'Missing income statement' + notRegisteredString,
            icon: MetricsIconEnum.Error,
            color: MetricsColorEnum.Error,
            groupingName: 'Missing income statement',
          });
          continue;
        }

        const secondLast = statements[1] ?? null;
        if (lastMonthStatement && secondLast) {
          const balance = lastMonthStatement.balance + secondLast.balance;
          if (balance < 0) {
            results.push({
              ...(notificationBase as NotificationDisplay),
              subject: `Programme lost R${balance} in period`,
              icon: MetricsIconEnum.Error,
              color: MetricsColorEnum.Error,
              groupingName: 'Programme losing money',
            });
            continue;
          }
          if (balance > 0) {
            results.push({
              ...(notificationBase as NotificationDisplay),
              subject: `Made R${balance} profit`,
              icon: MetricsIconEnum.Success,
              color: MetricsColorEnum.Success,
              groupingName: 'Programme making profit',
            });
            continue;
          }
        }

        const practitionerClassroomGroupIds = classroomGroups
          .filter((x) => x.userId === practitioner.userId && x.isActive)
          .map((x) => x.id);
        const numberOfLearners = classroomGroups
          .filter((g) => practitionerClassroomGroupIds.includes(g.id))
          .reduce((acc, g) => acc + (g.learners?.length ?? 0), 0);
        if (numberOfLearners < 5) {
          results.push({
            ...(notificationBase as NotificationDisplay),
            subject: 'Less than 5 children registered' + notRegisteredString,
            icon: MetricsIconEnum.Error,
            color: MetricsColorEnum.Error,
            groupingName: 'Less than 5 children registered',
          });
          continue;
        }
      }

      // MISSING ATTENDANCE REGISTERS

      if (
        attendanceService &&
        typeof (attendanceService as any).getMissingAttendanceReports ===
          'function'
      ) {
        const missing = (attendanceService as any).getMissingAttendanceReports(
          practitioner,
          previousMonthStart,
          previousMonthEnd
        );
        if (missing > 0) {
          results.push({
            ...(notificationBase as NotificationDisplay),
            subject: 'Missing attendance registers' + notRegisteredString,
            icon: MetricsIconEnum.Error,
            color: MetricsColorEnum.Error,
            groupingName: 'Missing attendance registers',
          });
          continue;
        }
      }

      // ATTENDANCE PERCENTAGE checks (child attendance)
      if (!practitioner.isTrainee) {
        if (
          attendanceService &&
          typeof (attendanceService as any).getAttendancePercentileByParent ===
            'function'
        ) {
          const percent = (
            attendanceService as any
          ).getAttendancePercentileByParent(
            practitioner.userId,
            previousMonthStart,
            previousMonthEnd
          );
          if (percent < 60) {
            results.push({
              ...(notificationBase as NotificationDisplay),
              subject:
                `${percent}% child attendance in ${previousMonthStart.toLocaleString(
                  'default',
                  { month: 'short' }
                )}` + notRegisteredString,
              icon: MetricsIconEnum.Error,
              color: MetricsColorEnum.Error,
              notes: 'Improve attendance',
              groupingName: '50% child attendance last month',
            });
            continue;
          }
          if (percent < 80) {
            results.push({
              ...(notificationBase as NotificationDisplay),
              subject:
                `${percent}% child attendance in ${previousMonthStart.toLocaleString(
                  'default',
                  { month: 'short' }
                )}` + notRegisteredString,
              icon: MetricsIconEnum.Warning,
              color: MetricsColorEnum.Warning,
              notes: 'Improve attendance',
              groupingName: '70% child attendance',
            });
            continue;
          }
          if (percent >= 80) {
            results.push({
              ...(notificationBase as NotificationDisplay),
              subject:
                `${percent}% child attendance in ${previousMonthStart.toLocaleString(
                  'default',
                  { month: 'short' }
                )}` + notRegisteredString,
              icon: MetricsIconEnum.Success,
              color: MetricsColorEnum.Success,
              groupingName: '80% child attendance',
            });
            continue;
          }
        }
      }

      // NOT ASSIGNED TO CLUB
      if (!getClubMemberForPractitioner(practitioner.id)) {
        results.push({
          ...(notificationBase as NotificationDisplay),
          subject: 'Not assigned to a club' + notRegisteredString,
          icon: MetricsIconEnum.Error,
          color: MetricsColorEnum.Error,
          groupingName: 'Not assigned to a club',
        });
        continue;
      }

      // MISSED CLUB MEETINGS
      const clubMember = getClubMemberForPractitioner(practitioner.id);
      if (clubMember) {
        const lastMonthClubMeetings = clubMeetingRegisters.filter(
          (m) =>
            m.practitionerId === practitioner.id &&
            m.attended === false &&
            toDate(m.attendedDate) &&
            toDate(m.attendedDate)!.getMonth() === now.getMonth() &&
            toDate(m.attendedDate)!.getFullYear() === now.getFullYear()
        );
        if (lastMonthClubMeetings.length > 0) {
          const practitionerClubAbsenceDictionaryCount =
            clubMeetingRegisters.filter(
              (x) =>
                x.attended === false &&
                toDate(x.attendedDate) &&
                toDate(x.attendedDate)!.getFullYear() === now.getFullYear() &&
                x.practitionerId === practitioner.id
            ).length;
          const missedMeetings = practitionerClubAbsenceDictionaryCount || 1;
          results.push({
            ...(notificationBase as NotificationDisplay),
            subject:
              `Missed ${missedMeetings} club meetings` + notRegisteredString,
            icon: MetricsIconEnum.Error,
            color: MetricsColorEnum.Error,
            groupingName: 'Missed club meetings',
          });
          continue;
        }
      }

      // BUSINESS SKILLS TRAINING DUE
      if (!practitioner.attendedBusinessSkills) {
        results.push({
          ...(notificationBase as NotificationDisplay),
          subject: `Business skills training due` + notRegisteredString,
          icon: MetricsIconEnum.Warning,
          color: MetricsColorEnum.Warning,
          groupingName: 'Business skills training due',
        });
        continue;
      }

      // CHILD PROGRESS TRAINING DUE
      if (!practitioner.attendedChildProgress) {
        results.push({
          ...(notificationBase as NotificationDisplay),
          subject: `Child progress training due` + notRegisteredString,
          icon: MetricsIconEnum.Warning,
          color: MetricsColorEnum.Warning,
          groupingName: 'Child progress training due',
        });
        continue;
      }

      // NEW SMARTSTARTER
      if (
        practitioner.isRegistered &&
        practitioner.startDate &&
        toDate(practitioner.startDate)! >
          new Date(now.getTime() - 3 * 30 * 24 * 3600 * 1000)
      ) {
        results.push({
          ...(notificationBase as NotificationDisplay),
          subject: 'New SmartStarter' + notRegisteredString,
          icon: MetricsIconEnum.Success,
          color: MetricsColorEnum.Success,
          groupingName: 'New SmartStarter',
        });
        continue;
      }

      // CLASSROOM NAME final default
      const classroom = classrooms.find(
        (c) => c.userId === practitioner.userId
      );
      results.push({
        ...(notificationBase as NotificationDisplay),
        subject: classroom?.name ?? 'Unknown classroom' + notRegisteredString,
        icon: MetricsIconEnum.Success,
        color: MetricsColorEnum.Success,
        groupingName: 'Classroom',
      });
    }

    return results;

    function trainingOverdueCountApprox(
      pract: Practitioner,
      traineeVisits: Visit[],
      now: Date
    ) {
      const requiredVisitNames = [
        'ss_coach_visit',
        'starter_license',
        'smart_space',
        'three_children_registered',
      ];
      let overdue = 0;
      for (const name of requiredVisitNames) {
        const v = traineeVisits.find((x) => x.visitType?.name?.includes(name));
        if (!v) overdue++;
        else if (!v.attended && toDate(v.plannedVisitDate)! < now) overdue++;
      }
      return overdue;
    }
  }, [
    practitioners,
    mode,
    now,
    removals,
    licenses,
    visitData,
    pqaRatings,
    absentees,
    clubMembers,
    clubMeetingRegisters,
    classrooms,
    classroomGroups,
    documents,
    childProgressReportService,
    attendanceService,
    incomeStatements,
  ]);
}

export default usePractitionerNotifications;
